import { useEffect, useState } from "react";
import api, { eur, hrs } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { KpiCard, PageHeader } from "@/components/Common";
import { BrandLogo, themeBg } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Users, Building2, MapPin, CheckSquare, Clock, Euro, TrendingUp, Percent, Timer, CalendarDays, Plane, HeartPulse, Target, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { DashboardCustomise, availableWidgets, orderedWidgets } from "@/components/DashboardCustomise";
import { SlidersHorizontal, ClipboardList } from "lucide-react";

const COST_COLORS = ["#0F172A", "#F59E0B", "#0EA5E9", "#10B981", "#E11D48"];

const eurK = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1000000) return `€${(n / 1000000).toFixed(2)}M`;
  if (v >= 10000) return `€${(n / 1000).toFixed(1)}k`;
  return eur(n);
};

const fmtD = (s) => {
  if (!s) return "";
  const [y, m, dd] = s.split("-").map(Number);
  if (!y || !m || !dd) return s;
  return new Date(y, m - 1, dd).toLocaleDateString("en-IE", { day: "numeric", month: "short" });
};

function Donut({ data, colors, centerLabel, centerValue }) {
  return (
    <div className="relative h-52 min-w-0">
      <ResponsiveContainer width="99%" height="100%" minWidth={0}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2} stroke="none" isAnimationActive={false}>
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
          <Legend verticalAlign="bottom" height={30} wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ top: "-18px" }}>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">{centerLabel}</div>
        <div className="num text-xl font-black text-zinc-900">{centerValue}</div>
      </div>
    </div>
  );
}

function SiteList({ title, rows, tone }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md">
      <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-wider text-zinc-500">{title}</div>
      <div className="divide-y divide-zinc-50">
        {(!rows || rows.length === 0) && <div className="px-4 py-3 text-sm text-zinc-400">None</div>}
        {rows?.map((r) => (
          <Link to="/admin/site-profitability" key={r.site.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-zinc-50 transition-colors">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{r.site.name}</div>
              <div className="text-xs text-zinc-500 truncate">{r.site.customer_name}</div>
            </div>
            <div className="text-right shrink-0">
              <div className={`num text-sm font-bold ${tone === "bad" ? "text-rose-600" : "text-emerald-600"}`}>{eur(r.profit)}</div>
              <div className="num text-xs text-zinc-500">{hrs(r.margin)}% margin</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, company } = useAuth();
  const brand = user?.role === "amden_super" && !user?._impersonation?.active ? { name: "All Companies" } : company;
  const [d, setD] = useState(null);
  const [trends, setTrends] = useState([]);
  const [seat, setSeat] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [custOpen, setCustOpen] = useState(false);
  const [formsSum, setFormsSum] = useState(null);
  const em = company?.enabled_modules || {};
  const on = (m) => em[m] !== false;
  useEffect(() => {
    api.get("/dashboard/prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({}));
    if (on("/admin/forms")) api.get("/dashboard/forms-summary").then((r) => setFormsSum(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const avail = availableWidgets(em);
  let visible = orderedWidgets(avail, prefs).filter((w) => !(prefs?.hidden || []).includes(w.id));
  if (visible.length === 0) visible = orderedWidgets(avail, {});
  const w = (id, node) => { const i = visible.findIndex((x) => x.id === id); return i === -1 ? null : <div key={id} data-testid={`dash-section-${id}`} style={{ order: i }}>{node}</div>; };
  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {});
    api.get("/dashboard/trends").then((r) => setTrends(r.data)).catch(() => {});
    api.get("/seat-usage").then((r) => setSeat(r.data)).catch(() => {});
  }, []);

  const totalRevenue = d ? d.labour_revenue + d.materials_revenue : 0;
  const totalCost = d ? d.labour_cost + d.employer_prsi + d.holiday_accrual + d.materials_cost : 0;
  const pnlData = d ? [
    { name: "Gross Profit", value: Math.max(0, d.gross_profit) },
    { name: "Total Cost", value: Math.max(0, totalCost) },
  ] : [];
  const costData = d ? [
    { name: "Labour", value: Math.max(0, d.labour_cost) },
    { name: "PRSI", value: Math.max(0, d.employer_prsi) },
    { name: "Holiday", value: Math.max(0, d.holiday_accrual) },
    { name: "Materials", value: Math.max(0, d.materials_cost) },
  ] : [];

  return (
    <AdminLayout nav={adminNav} title="Management Dashboard">
      <div className="flex justify-end -mt-2 mb-3"><Button data-testid="dashboard-customise-btn" variant="outline" size="sm" onClick={() => setCustOpen(true)}><SlidersHorizontal className="h-4 w-4 mr-1" />Customise</Button></div>
      <DashboardCustomise open={custOpen} onOpenChange={setCustOpen} avail={avail} prefs={prefs} onSaved={setPrefs} isSuper={user?.role === "amden_super" || !!user?._impersonation?.active} />
      {!d ? <div className="text-zinc-400">Loading…</div> : (
        <div className="flex flex-col gap-6">
          {seat && seat.limit != null && seat.used >= seat.limit - 1 && (
            <div data-testid="seat-warning" className={`rounded-md border p-4 text-sm ${seat.used >= seat.limit ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              <span className="font-semibold">{seat.used >= seat.limit ? "User seat limit reached" : "Almost at your user seat limit"}</span> — {seat.used} of {seat.limit} seats used.{seat.used >= seat.limit ? " Contact Amden to add more seats." : " You have 1 seat remaining."}
            </div>
          )}
          {on("/admin/compliance") && d.subcontractor_insurance?.length > 0 && (
            <Link to="/admin/compliance" data-testid="flag-sub-insurance" className="block rounded-md border border-rose-200 bg-rose-50 p-4 hover:border-rose-400 transition-colors">
              <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm"><AlertTriangle className="h-4 w-4" /> {d.subcontractor_insurance.length} subcontractor{d.subcontractor_insurance.length > 1 ? "s" : ""} with expired or missing insurance</div>
              <div className="text-xs text-rose-700 mt-1 truncate">{d.subcontractor_insurance.map((s) => `${s.name} (${s.status})`).join(", ")}</div>
            </Link>
          )}
          {on("/admin/timesheets") && ((d.employees_no_rate?.length || 0) + (d.customers_no_charge_rate?.length || 0)) > 0 && (
            <div data-testid="dashboard-flags" className="rounded-md border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2"><AlertTriangle className="h-4 w-4" /> Data needs attention</div>
              <div className="grid sm:grid-cols-2 gap-3 [&>*]:min-w-0">
                {d.employees_no_rate?.length > 0 && (
                  <Link to="/admin/employees" data-testid="flag-employees" className="block rounded-md bg-white border border-amber-200 p-3 hover:border-amber-400 transition-colors">
                    <div className="text-sm font-bold text-zinc-800">{d.employees_no_rate.length} employee{d.employees_no_rate.length > 1 ? "s" : ""} with no pay rate</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{d.employees_no_rate.map((e) => e.name).join(", ")}</div>
                    <div className="text-[11px] text-amber-600 font-semibold mt-1">Their labour won't show as a cost →</div>
                  </Link>
                )}
                {d.customers_no_charge_rate?.length > 0 && (
                  <Link to="/admin/customers" data-testid="flag-customers" className="block rounded-md bg-white border border-amber-200 p-3 hover:border-amber-400 transition-colors">
                    <div className="text-sm font-bold text-zinc-800">{d.customers_no_charge_rate.length} customer{d.customers_no_charge_rate.length > 1 ? "s" : ""} with no charge rate</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{d.customers_no_charge_rate.map((c) => c.name).join(", ")}</div>
                    <div className="text-[11px] text-amber-600 font-semibold mt-1">Revenue may be under-charged →</div>
                  </Link>
                )}
              </div>
            </div>
          )}
          {w("hero", <div className="relative overflow-hidden rounded-xl text-white p-6 md:p-8" style={{ backgroundColor: themeBg(company?.primary_color) }}>
            <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-amber-500/10" />
            <div className="absolute right-24 bottom-0 h-40 w-40 rounded-full bg-sky-500/10" />
            <div className="relative z-10 flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-5">
                  <BrandLogo company={brand} dark className="h-8" />
                  <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-400">Management Overview · This Month</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Revenue</div>
                    <div className="num text-2xl font-black">{eur(totalRevenue)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Total Cost</div>
                    <div className="num text-2xl font-black">{eur(totalCost)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Gross Profit</div>
                    <div className="num text-2xl font-black text-amber-500">{eur(d.gross_profit)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Gross Margin</div>
                    <div className="num text-2xl font-black text-amber-500">{hrs(d.gross_margin)}%</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-6 max-w-md">
                  <div className="rounded-lg bg-white/5 p-3"><div className="num text-xl font-bold">{d.active_employees}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400">Employees</div></div>
                  <div className="rounded-lg bg-white/5 p-3"><div className="num text-xl font-bold">{d.active_sites}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400">Active Sites</div></div>
                  <div className="rounded-lg bg-white/5 p-3"><div className="num text-xl font-bold">{hrs(d.month_hours)}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400">Hours (mo)</div></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 lg:w-[440px] bg-white rounded-lg p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 text-center mb-1 mt-1">Revenue Split</div>
                  <Donut data={pnlData} colors={["#F59E0B", "#0F172A"]} centerLabel="Revenue" centerValue={eurK(totalRevenue)} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 text-center mb-1 mt-1">Cost Breakdown</div>
                  <Donut data={costData} colors={COST_COLORS} centerLabel="Cost" centerValue={eurK(totalCost)} />
                </div>
              </div>
            </div>
          </div>)}

          {w("reminders", ((on("/admin/compliance") && d.expiring_docs > 0) || (on("/admin/sick-leave") && d.missing_sick_certs > 0)) ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {on("/admin/compliance") && <Link to="/admin/compliance" data-testid="reminder-docs" className={`rounded-md border p-4 flex items-center justify-between transition-colors ${d.expiring_docs > 0 ? "bg-amber-50 border-amber-200 hover:border-amber-300" : "bg-white border-zinc-200"}`}>
                <div><div className="text-sm font-semibold text-amber-800">Certifications expiring / expired</div><div className="text-xs text-zinc-500">Safe Pass, electrical quals & more within 60 days</div></div>
                <div className="num text-2xl font-black text-amber-600">{d.expiring_docs}</div>
              </Link>}
              {on("/admin/sick-leave") && <Link to="/admin/sick-leave" data-testid="reminder-certs" className={`rounded-md border p-4 flex items-center justify-between transition-colors ${d.missing_sick_certs > 0 ? "bg-rose-50 border-rose-200 hover:border-rose-300" : "bg-white border-zinc-200"}`}>
                <div><div className="text-sm font-semibold text-rose-800">Sick certs missing</div><div className="text-xs text-zinc-500">Absences without an uploaded sick cert</div></div>
                <div className="num text-2xl font-black text-rose-600">{d.missing_sick_certs}</div>
              </Link>}
            </div>
          ) : <div data-testid="reminders-clear" className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">All clear: nothing needs attention right now.</div>)}
          {w("forms", formsSum && (
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/admin/forms" data-testid="dash-forms-due" className="rounded-md border border-zinc-200 bg-white p-4 flex items-center justify-between hover:border-amber-300"><div><div className="text-sm font-semibold flex items-center gap-1.5"><ClipboardList className="h-4 w-4" />Checklists due</div><div className="text-xs text-zinc-500">Not yet done this period</div></div><div className="num text-2xl font-black">{formsSum.forms_due}</div></Link>
              <Link to="/admin/forms?tab=submissions" data-testid="dash-forms-failed" className={`rounded-md border p-4 flex items-center justify-between ${formsSum.failed_open ? "bg-rose-50 border-rose-200" : "bg-white border-zinc-200"}`}><div><div className="text-sm font-semibold text-rose-800">Failed items open</div><div className="text-xs text-zinc-500">Waiting to be resolved</div></div><div className="num text-2xl font-black text-rose-600">{formsSum.failed_open}</div></Link>
              <div className="rounded-md border border-zinc-200 bg-white p-4 flex items-center justify-between"><div><div className="text-sm font-semibold">Published forms</div><div className="text-xs text-zinc-500">Live for staff</div></div><div className="num text-2xl font-black">{formsSum.published}</div></div>
            </div>
          ))}
          {w("whos_out", <div data-testid="whos-out" className="bg-white border border-zinc-200 rounded-md">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Who's Out Today</span>
              <Link to="/admin/leave-calendar" className="text-xs text-sky-600 hover:underline">Leave calendar →</Link>
            </div>
            <div className="p-4">
              {(!d.employees_absent || d.employees_absent.length === 0) ? (
                <div className="text-sm text-zinc-400" data-testid="whos-out-empty">Everyone's in today.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {d.employees_absent.map((e, i) => (
                    <div key={i} data-testid={`absent-chip-${i}`} className={`flex items-center gap-2 rounded-full border pl-2.5 pr-3 py-1 text-sm ${e.kind === "sick" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-sky-50 border-sky-200 text-sky-700"}`}>
                      {e.kind === "sick" ? <HeartPulse className="h-3.5 w-3.5 shrink-0" /> : <Plane className="h-3.5 w-3.5 shrink-0" />}
                      <span className="font-medium">{e.name}</span>
                      <span className="text-xs opacity-70">{e.type}{e.back ? ` · back ${fmtD(e.back)}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>)}

          {w("trend", trends.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Profit &amp; Margin Trend (6 months)</h2>
              <div className="h-64 min-w-0">
                <ResponsiveContainer width="99%" height="100%" minWidth={0}>
                  <LineChart data={trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, n) => n === "margin" ? `${v}%` : eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="l" type="monotone" dataKey="profit" name="Gross Profit" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    <Line yAxisId="r" type="monotone" dataKey="margin" name="Margin %" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
          {w("kpis", <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testId="kpi-employees" label="Active Employees" value={d.active_employees} icon={Users} />
            {on("/admin/customers") && <KpiCard testId="kpi-customers" label="Active Customers" value={d.active_customers} icon={Building2} />}
            {on("/admin/sites") && <KpiCard testId="kpi-sites" label="Active Sites" value={d.active_sites} icon={MapPin} />}
            {on("/admin/approvals") && <KpiCard testId="kpi-awaiting" label="Awaiting Approval" value={d.awaiting_approval} icon={CheckSquare} accent />}
            {on("/admin/timesheets") && <>
            <KpiCard testId="kpi-week-hours" label="Hours This Week" value={hrs(d.week_hours)} icon={Clock} />
            <KpiCard testId="kpi-month-hours" label="Hours This Month" value={hrs(d.month_hours)} icon={Clock} />
            <KpiCard testId="kpi-overtime" label="Overtime Hours" value={hrs(d.overtime_hours)} icon={Timer} />
            </>}
            {on("/admin/holidays") && <KpiCard testId="kpi-pending-leave" label="Pending Leave" value={d.pending_leave} icon={CalendarDays} />}
            {on("/admin/compliance") && <KpiCard testId="kpi-expiring" label="Certs Expiring (60d)" value={d.expiring_docs} icon={AlertTriangle} />}
          </div>)}

          {w("financial", <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Financial (this month, approved)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard testId="kpi-labour-rev" label="Labour Revenue" value={eur(d.labour_revenue)} icon={Euro} />
              <KpiCard testId="kpi-labour-cost" label="Labour Cost" value={eur(d.labour_cost)} icon={Euro} />
              <KpiCard testId="kpi-prsi" label="Employer PRSI" value={eur(d.employer_prsi)} icon={Euro} />
              <KpiCard testId="kpi-holiday" label="Holiday Accrual" value={eur(d.holiday_accrual)} icon={Euro} />
              {on("/admin/materials") && <><KpiCard testId="kpi-materials" label="Materials Cost" value={eur(d.materials_cost)} icon={Euro} />
              <KpiCard testId="kpi-materials-rev" label="Materials Revenue" value={eur(d.materials_revenue)} icon={Euro} /></>}
              <KpiCard testId="kpi-gp" label="Gross Profit" value={eur(d.gross_profit)} icon={TrendingUp} accent />
              <KpiCard testId="kpi-margin" label="Gross Margin" value={`${hrs(d.gross_margin)}%`} icon={Percent} accent />
            </div>
          </div>)}

          {w("sites", <div className="grid md:grid-cols-2 gap-4">
            <SiteList title="Most Profitable Sites" rows={d.top_sites} />
            <SiteList title="Lowest Performing Sites" rows={d.bottom_sites} tone="bad" />
            <SiteList title="Sites Over Budget" rows={d.sites_over_budget} tone="bad" />
            <SiteList title="Sites Below Target Margin" rows={d.sites_below_target} tone="bad" />
          </div>)}

          {w("forecast", <div className="bg-white border border-zinc-200 rounded-md" data-testid="forecast-section">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-1">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Target className="h-4 w-4" /> Forecast Profitability</span>
              <span className="text-[11px] text-zinc-400">Projected final profit from work booked so far vs budget · worst-tracking first</span>
            </div>
            {(!d.forecast || d.forecast.length === 0) ? (
              <div className="px-4 py-4 text-sm text-zinc-400" data-testid="forecast-empty">No sites with booked hours and a budget yet. Set site budgets and approve timesheets to see forecasts.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-4 py-2">Site</th>
                    <th className="px-4 py-2 text-right">Hours</th>
                    <th className="px-4 py-2 w-44">Budget used</th>
                    <th className="px-4 py-2 text-right">Profit so far</th>
                    <th className="px-4 py-2 text-right">Budget profit</th>
                    <th className="px-4 py-2 text-right">Projected profit</th>
                    <th className="px-4 py-2 text-right">vs Budget</th>
                  </tr></thead>
                  <tbody className="divide-y divide-zinc-50">
                    {d.forecast.map((f) => {
                      const projTone = f.projected_profit < 0 ? "text-rose-600" : (f.variance < 0 ? "text-amber-600" : "text-emerald-600");
                      const varTone = f.variance < 0 ? "text-rose-600" : "text-emerald-600";
                      const pct = Math.min(100, Math.max(0, f.progress_pct));
                      return (
                        <tr key={f.site.id} data-testid={`forecast-row-${f.site.id}`} className="hover:bg-zinc-50">
                          <td className="px-4 py-2.5"><div className="font-medium truncate max-w-[200px]">{f.site.name}</div><div className="text-xs text-zinc-500 truncate max-w-[200px]">{f.site.customer_name}</div></td>
                          <td className="px-4 py-2.5 text-right num">{hrs(f.hours)}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 flex-1 rounded-full bg-zinc-100 overflow-hidden"><div className={`h-full ${f.progress_pct > 100 ? "bg-rose-500" : "bg-slate-800"}`} style={{ width: `${pct}%` }} /></div>
                              <span className="num text-xs text-zinc-500 w-11 text-right">{hrs(f.progress_pct)}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right num">{eur(f.actual_profit)}</td>
                          <td className="px-4 py-2.5 text-right num text-zinc-500">{eur(f.budget_profit)}</td>
                          <td className={`px-4 py-2.5 text-right num font-bold ${projTone}`}>{eur(f.projected_profit)}<div className="text-xs font-normal text-zinc-400">{hrs(f.projected_margin)}% margin</div></td>
                          <td className={`px-4 py-2.5 text-right num font-semibold ${varTone}`}>{f.variance >= 0 ? "+" : ""}{eur(f.variance)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>)}
        </div>
      )}
    </AdminLayout>
  );
}
